import { icon } from "@fortawesome/fontawesome-svg-core/import.macro"
import clsx from "clsx"
import { useEffect, useMemo, useState } from "react"

import useAppHotkey from "@hooks/useAppHotkey"
import useHasAccess from "@hooks/useHasAccess"
import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useRouter from "@hooks/useRouter"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { ifSpaceOrEnter } from "@utils/keyboard"
import replaceSlugs from "@utils/replaceSlugs"

import { HotKey, Icon } from "@atoms"

import { OverflowMenu } from "@molecules"
import { Item, SubItem } from "@molecules/OverflowMenu/OverflowMenu.types"

import { BILLS_ROUTES } from "@routes/bills"
import { CLIENTS_ROUTES } from "@routes/clients"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./SideBarCreateButton.module.scss"
import { SideBarCreateButtonProps } from "./SideBarCreateButton.types"

const OPEN_OVERFLOW_MENU_HOTKEY = "c"

export default function SideBarCreateButton(props: SideBarCreateButtonProps) {
    const { isDisabled = false } = props

    const { user } = useUser()
    const router = useRouter()

    const { hasEntitlement, hasPermission } = useHasAccess()

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [userIsInteracting, setUserIsInteracting] = useState<boolean>(false)

    const openOverflowMenu = () => (!isDisabled ? setIsOpen(true) : null)
    const closeOverflowMenu = () => setIsOpen(false)

    const onObjectSelect = (item: Item | SubItem) => {
        const route = replaceSlugs(item.value, { service_company_slug: user?.service_company?.slug ?? "undefined" })
        router.push(route)
    }

    const onHoverActiveOrFocusVisible = () => {
        setUserIsInteracting(true)
    }

    const onMouseLeave = () => {
        setUserIsInteracting(false)
    }

    const { setIsAnyDialogOpen } = useIsAnyDialogOpen()

    useAppHotkey(OPEN_OVERFLOW_MENU_HOTKEY, () => openOverflowMenu(), { enabled: !isDisabled }, [isDisabled])

    useEffect(() => {
        setIsAnyDialogOpen(isOpen)
    }, [isOpen])

    const [
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.CREATE,
        JOBS_ROUTES.CREATE,
        INVOICES_ROUTES.CREATE,
        PURCHASE_ORDER_ROUTES.CREATE,
        BILLS_ROUTES.CREATE,
        VENDOR_ROUTES.CREATE,
        CLIENTS_ROUTES.CREATE,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
    ])

    const filteredObjectCreateItems = useMemo(() => {
        const items: Item[] = []

        if (
            hasEntitlement("entitlement_estimates_enabled") &&
            hasPermission("estimates_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: icon({ name: "file-lines", style: "light", family: "sharp" }),
                value: estimateCreateRoute,
                label: "Estimate",
                isLink: true,
            })
        }

        if (
            hasEntitlement("entitlement_jobs_enabled") &&
            hasPermission("jobs_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: icon({ name: "wrench-simple", style: "light", family: "sharp" }),
                value: jobCreateRoute,
                label: "Job",
                isLink: true,
            })
        }

        if (
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: icon({ name: "circle-dollar", style: "light", family: "sharp" }),
                value: invoiceCreateRoute,
                label: "Invoice",
                isLink: true,
            })
        }

        if (
            hasEntitlement("entitlement_purchase_orders_enabled") &&
            hasPermission("purchase_orders_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: icon({ name: "basket-shopping", style: "light", family: "sharp" }),
                value: purchaseOrderCreateRoute,
                label: "Purchase Order",
                isLink: true,
            })
        }

        if (
            hasEntitlement("entitlement_bills_enabled") &&
            hasPermission("bills_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: icon({ name: "file-invoice-dollar", style: "light", family: "sharp" }),
                value: billCreateRoute,
                label: "Bill",
                isLink: true,
            })
        }

        if (hasPermission("vendors_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: icon({ name: "store", style: "light", family: "sharp" }),
                value: vendorCreateRoute,
                label: "Vendor",
                isLink: true,
            })
        }

        if (hasPermission("clients_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: icon({ name: "user-circle", style: "light", family: "sharp" }),
                value: clientCreateRoute,
                label: "Client",
                isLink: true,
            })
        }

        if (hasPermission("pricebook_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                value: "pricebook_item",
                label: "Pricebook Item",
                icon: icon({ name: "tag", style: "light", family: "sharp" }),
                subItems: [
                    {
                        value: pricebookItemServiceCreateRoute,
                        label: "Service",
                        isLink: true,
                    },
                    {
                        value: pricebookItemPartOrMaterialCreateRoute,
                        label: "Part or Material",
                        isLink: true,
                    },
                    {
                        value: pricebookItemMiscellaneousCreateRoute,
                        label: "Miscellaneous",
                        isLink: true,
                    },
                    {
                        value: pricebookItemDiscountCreateRoute,
                        label: "Discount",
                        isLink: true,
                    },
                    ...(isUsingTaxRates
                        ? [
                              {
                                  value: pricebookItemTaxRatesCreateRoute,
                                  label: "Tax Rate",
                                  isLink: true,
                              },
                          ]
                        : []),
                ],
            })
        }

        return items
    }, [hasEntitlement, hasPermission, isUsingTaxRates])

    return (
        <div className={styles.base}>
            <div
                className={clsx(styles.trigger, {
                    [styles.active]: isOpen,
                    [styles.isDisabled]: isDisabled,
                })}
                tabIndex={0}
                role="button"
                onClick={openOverflowMenu}
                onKeyDown={(e) => ifSpaceOrEnter(e, openOverflowMenu)}
                onMouseOver={onHoverActiveOrFocusVisible}
                onFocus={onHoverActiveOrFocusVisible}
                onMouseOut={onMouseLeave}
            >
                <div className={styles.iconAndText}>
                    <div className={styles.iconWrapper}>
                        <Icon
                            className={styles.icon}
                            icon={icon({ name: "circle-plus", style: "solid", family: "sharp" })}
                            size={16}
                        />
                    </div>
                    <div className={styles.label}>
                        <span>Create</span>
                    </div>
                </div>
                {!isDisabled && <HotKey symbolName="C" variant={isOpen || userIsInteracting ? "solid" : "subtle"} />}
            </div>
            <div className={styles.overflowMenu}>
                <OverflowMenu
                    triggerClassName={styles.overflowMenu}
                    onClose={closeOverflowMenu}
                    isOpen={isOpen}
                    onSelect={onObjectSelect}
                    startingPoint="right"
                    items={filteredObjectCreateItems}
                />
            </div>
        </div>
    )
}
